import type { TransferBatchStatusEnum } from '@frameio/graphql-client/generated';

// A type that enforces that the keys and values in an object
// are the same and match string literals of the provided generic.
type EnforceMatchingKeysAndValues<T extends string> = {
  [K in T]: K;
};

/**
 * Batches can have the following states:
 * - `IDLE`: The default resting state before `start()` is called.
 * - `PROCESSING`: The batch has started processing `UploadItem`s and may have put file parts into the S3 queue, but before bytes are uploaded to S3.
 * - `UPLOADING`: Actively uploading or have uploaded some files to S3.
 * - `DOWNLOADING`: Actively downloading files from S3.
 * - `TRANSFERS_COMPLETE`: The batch is complete
 * - `CHECKSUMMING_IN_PROGRESS`: Files checksum verification in progress.
 * - `CHECKSUMS_COMPLETE`: [**not supported**] All tasks have been checksummed
 * - `ALL_THUMBNAILS_GENERATED`: All files are previewable (see: TransferJobTaskStatus.CANPREVIEW)
 * - `CAN_VIEW_ALL`: All assets have the minimum transcodes needed to view.
 * - `COMPLETE`: [**not supported**] All assets are completely transcoded.
 * - `SUCCEEDED`: The entire batch succeeded.
 * - `CANCELED`: The batch was canceled
 * - `FAILED`: The entire batch Failed.
 *
 * - Happy path for batches:
 *   - IDLE -> PROCESSING -> UPLOADING -> TRANSFERS_COMPLETE -> ALL_THUMBNAILS_GENERATED -> SUCCEEDED
 *
 * - Happy path for folder batches:
 *   - IDLE -> PROCESSING -> SUCCEEDED
 */
export const TransferBatchStatus: EnforceMatchingKeysAndValues<TransferBatchStatusEnum> =
  {
    IDLE: 'IDLE',
    PROCESSING: 'PROCESSING',
    UPLOADING: 'UPLOADING',
    DOWNLOADING: 'DOWNLOADING',
    TRANSFERS_COMPLETE: 'TRANSFERS_COMPLETE',
    CHECKSUMMING_IN_PROGRESS: 'CHECKSUMMING_IN_PROGRESS',
    CHECKSUMS_COMPLETE: 'CHECKSUMS_COMPLETE',
    ALL_THUMBNAILS_GENERATED: 'ALL_THUMBNAILS_GENERATED',
    CAN_VIEW_ALL: 'CAN_VIEW_ALL',
    COMPLETE: 'COMPLETE',
    SUCCEEDED: 'SUCCEEDED',
    CANCELED: 'CANCELED',
    FAILED: 'FAILED',
    CANCELING: 'CANCELING',
  };

export const TERMINAL_TRANSFER_STATUSES: TransferBatchStatusEnum[] = [
  TransferBatchStatus.SUCCEEDED,
  TransferBatchStatus.FAILED,
  TransferBatchStatus.CANCELED,
];

export const TRANSCODING_TRANSFER_STATUSES: TransferBatchStatusEnum[] = [
  TransferBatchStatus.TRANSFERS_COMPLETE,
  TransferBatchStatus.CHECKSUMS_COMPLETE,
  TransferBatchStatus.CHECKSUMMING_IN_PROGRESS,
  TransferBatchStatus.ALL_THUMBNAILS_GENERATED,
];

export const TRANSFERRING_TRANSFER_STATUSES: TransferBatchStatusEnum[] = [
  TransferBatchStatus.UPLOADING,
  TransferBatchStatus.DOWNLOADING,
];

export const QUEUED_TRANSFER_STATUSES: TransferBatchStatusEnum[] = [
  TransferBatchStatus.IDLE,
  TransferBatchStatus.PROCESSING,
];
